import { useState, useEffect, useCallback, useMemo } from 'react';
import { ACHIEVEMENTS } from '../config/onboardingMedia';
import api from '../utils/api';

const STORAGE_KEY = 'user_achievements';

/**
 * useAchievements - Hook for managing gamification achievements
 *
 * Tracks user achievements, points, and progress towards badges.
 * Syncs with backend and localStorage for persistence.
 */
export function useAchievements(userId) {
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load achievements from storage
  useEffect(() => {
    const loadAchievements = async () => {
      try {
        // Try localStorage first
        const stored = localStorage.getItem(`${STORAGE_KEY}_${userId}`);
        if (stored) {
          setUnlockedAchievements(JSON.parse(stored));
        }

        // Sync from backend
        try {
          const response = await api.get('/users/me/achievements');
          if (response.data?.achievements) {
            setUnlockedAchievements(response.data.achievements);
            localStorage.setItem(
              `${STORAGE_KEY}_${userId}`,
              JSON.stringify(response.data.achievements)
            );
          }
        } catch (err) {
          // Use localStorage as fallback if backend fails
          console.warn('Could not sync achievements from backend:', err.message);
        }
      } catch (err) {
        console.error('Failed to load achievements:', err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      loadAchievements();
    }
  }, [userId]);

  // Save achievements to storage
  const saveAchievements = useCallback(async (achievements) => {
    try {
      localStorage.setItem(`${STORAGE_KEY}_${userId}`, JSON.stringify(achievements));

      // Sync to backend
      try {
        await api.post('/users/me/achievements', { achievements });
      } catch (err) {
        // localStorage is fallback if backend fails
        console.warn('Could not sync achievements to backend:', err.message);
      }
    } catch (err) {
      console.error('Failed to save achievements:', err);
    }
  }, [userId]);

  // Calculate total points
  const totalPoints = useMemo(() => {
    return unlockedAchievements.reduce((total, achievementId) => {
      const achievement = ACHIEVEMENTS[achievementId];
      return total + (achievement?.points || 0);
    }, 0);
  }, [unlockedAchievements]);

  // Check if achievement is unlocked
  const isUnlocked = useCallback((achievementId) => {
    return unlockedAchievements.includes(achievementId);
  }, [unlockedAchievements]);

  // Unlock an achievement (with optional milestone check to avoid recursion)
  const unlock = useCallback((achievementId, skipMilestoneCheck = false) => {
    if (isUnlocked(achievementId)) {
      return false; // Already unlocked
    }

    const achievement = ACHIEVEMENTS[achievementId];
    if (!achievement) {
      console.warn(`Unknown achievement: ${achievementId}`);
      return false;
    }

    setUnlockedAchievements(prev => {
      const updated = [...prev, achievementId];
      saveAchievements(updated);

      // Check for milestone achievements (only if not already checking milestones)
      if (!skipMilestoneCheck) {
        // Check power_user milestone
        const currentPoints = updated.reduce((total, id) => {
          const a = ACHIEVEMENTS[id];
          return total + (a?.points || 0);
        }, 0);

        if (currentPoints >= 200 && !updated.includes('power_user')) {
          // Schedule milestone unlock to avoid state update during render
          setTimeout(() => {
            setUnlockedAchievements(curr => {
              if (!curr.includes('power_user')) {
                const withMilestone = [...curr, 'power_user'];
                saveAchievements(withMilestone);
                setPendingNotifications(n => [...n, {
                  ...ACHIEVEMENTS['power_user'],
                  unlockedAt: new Date().toISOString(),
                }]);
                return withMilestone;
              }
              return curr;
            });
          }, 0);
        }

        // Check uni_fund_expert milestone
        const allBasicAchievements = Object.keys(ACHIEVEMENTS).filter(
          id => !ACHIEVEMENTS[id].requirement
        );
        const hasAll = allBasicAchievements.every(id => updated.includes(id));
        if (hasAll && !updated.includes('uni_fund_expert')) {
          setTimeout(() => {
            setUnlockedAchievements(curr => {
              if (!curr.includes('uni_fund_expert')) {
                const withMilestone = [...curr, 'uni_fund_expert'];
                saveAchievements(withMilestone);
                setPendingNotifications(n => [...n, {
                  ...ACHIEVEMENTS['uni_fund_expert'],
                  unlockedAt: new Date().toISOString(),
                }]);
                return withMilestone;
              }
              return curr;
            });
          }, 0);
        }
      }

      return updated;
    });

    // Add to pending notifications
    setPendingNotifications(prev => [...prev, {
      ...achievement,
      unlockedAt: new Date().toISOString(),
    }]);

    return true;
  }, [isUnlocked, saveAchievements]);

  // Dismiss notification
  const dismissNotification = useCallback((achievementId) => {
    setPendingNotifications(prev =>
      prev.filter(n => n.id !== achievementId)
    );
  }, []);

  // Get progress towards an achievement (for achievements with requirements)
  const getProgress = useCallback((achievementId) => {
    const achievement = ACHIEVEMENTS[achievementId];
    if (!achievement?.requirement) {
      return isUnlocked(achievementId) ? 100 : 0;
    }

    if (achievement.requirement.totalPoints) {
      return Math.min(100, (totalPoints / achievement.requirement.totalPoints) * 100);
    }

    return 0;
  }, [totalPoints, isUnlocked]);

  // Get all achievements with unlock status
  const allAchievements = useMemo(() => {
    return Object.values(ACHIEVEMENTS).map(achievement => ({
      ...achievement,
      unlocked: isUnlocked(achievement.id),
      progress: getProgress(achievement.id),
    }));
  }, [isUnlocked, getProgress]);

  // Get achievements by category
  const getByCategory = useCallback((category) => {
    return allAchievements.filter(a => a.category === category);
  }, [allAchievements]);

  // Get a single achievement by ID
  const getAchievement = useCallback((achievementId) => {
    return ACHIEVEMENTS[achievementId] || null;
  }, []);

  // Track specific actions that can unlock achievements
  const trackAction = useCallback((action, count = 1) => {
    // This could be extended to track action counts in localStorage
    // and unlock achievements based on thresholds
    switch (action) {
      case 'search_used':
        // Track search usage for search_master achievement
        const searchCount = parseInt(localStorage.getItem('search_count') || '0', 10) + count;
        localStorage.setItem('search_count', searchCount.toString());
        if (searchCount >= 10 && !isUnlocked('search_master')) {
          unlock('search_master');
        }
        break;

      case 'keyboard_shortcut':
        const kbCount = parseInt(localStorage.getItem('keyboard_count') || '0', 10) + count;
        localStorage.setItem('keyboard_count', kbCount.toString());
        if (kbCount >= 25 && !isUnlocked('keyboard_ninja')) {
          unlock('keyboard_ninja');
        }
        break;

      case 'dark_mode_enabled':
        if (!isUnlocked('dark_side')) {
          unlock('dark_side');
        }
        break;

      case 'filter_saved':
        if (!isUnlocked('filter_pro')) {
          unlock('filter_pro');
        }
        break;

      default:
        break;
    }
  }, [unlock, isUnlocked]);

  return {
    // State
    unlockedAchievements,
    totalPoints,
    pendingNotifications,
    loading,

    // All achievements data
    allAchievements,
    getByCategory,

    // Actions
    unlock,
    isUnlocked,
    getProgress,
    getAchievement,
    dismissNotification,
    trackAction,
  };
}

export default useAchievements;
